import React, { useCallback, useEffect, useId, useRef } from 'react';
import PropTypes from 'prop-types';
import ReCAPTCHA from 'react-google-recaptcha';
import Input from 'src/components/form/floating-label/input';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import API from '../api/api';
import Modal from 'src/components/common/modal/modal';
import { env } from 'src/constants/env';
import { fireEvent } from 'src/utils/analytics/google';

// styles
import css from './propose-service.module.scss';
import 'src/utils/popup.scss';
import getValidationSchema from './validate';
import { toast } from 'react-toastify';
import { ONLINE } from 'src/constants/locations';

ProposeServicePopup.propTypes = {
  trigger: PropTypes.element,
  includeLocation: PropTypes.bool
};

export default function ProposeServicePopup({ trigger, includeLocation }) {
  const validationSchema = getValidationSchema();
  const captchaRef = useRef(null);
  const registerAndClearOnChange = function (inputName) {
    return register(inputName, {
      onChange: () => {
        if (errors[inputName]) {
          clearErrors(inputName);
        }
      }
    });
  };

  const methods = useForm({
    mode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      title: '',
      locationId: undefined,
      url: '',
      description: ''
    }
  });
  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    getValues,
    trigger: triggerValidation,
    formState: { errors, isSubmitSuccessful, defaultValues, isDirty, isValid }
  } = methods;
  const values = getValues();
  const isEdited = Object.keys(values).some((key) => values[key] !== defaultValues[key]);

  const resetCallback = useCallback(() => {
    return isSubmitSuccessful ? reset : () => {};
  }, [reset, isSubmitSuccessful]);

  useEffect(resetCallback, [resetCallback]);

  const onFormSubmit = async (data, close) => {
    try {
      const response = await API.proposeService(data);
      if (response.status === 201) {
        toast.success(`Пропозиція для створення послуги надіслана! Модератор опрацює Ваш запит`);
        reset();
        close();
        fireEvent('propose_service_form_submit', data);
      }
    } catch (error) {
      toast.error('Виникла помилка. Спробуйте, будь ласка, пізніше.');
    }
  };

  const titleInputId = useId();
  const locationInputId = useId();
  const urlInputId = useId();
  const descriptionInputId = useId();

  return (
    <Modal trigger={trigger} onClose={reset} nested closeOnDocumentClick={!isEdited}>
      {(close) => (
        <div className={css.proposeService}>
          <button className={css.close} onClick={close}>
            &times;
          </button>
          <FormProvider {...methods}>
            <form
              onSubmit={handleSubmit(async (data) => {
                const token = await captchaRef.current?.executeAsync();
                onFormSubmit({ ...data, captcha: token }, close);
              })}>
              <legend>
                <h1>Заповніть форму для створення послуги</h1>
              </legend>
              <fieldset>
                <Input
                  label='Назва послуги'
                  id={titleInputId}
                  name='title'
                  type='text'
                  error={errors?.title?.message}
                  required
                  placeholder='Моя послуга'
                  {...registerAndClearOnChange('title')}
                />
                <Input
                  label='Посилання на сайт'
                  id={urlInputId}
                  name='url'
                  type='text'
                  error={errors?.url?.message}
                  required
                  placeholder='http//:example.com'
                  {...registerAndClearOnChange('url')}
                />
                {includeLocation && (
                  <Input
                    label='Локація'
                    id={locationInputId}
                    name='locationId'
                    triggerValidation={triggerValidation}
                    clearErrors={clearErrors}
                    error={errors?.locationId?.message}
                    defaultLocation={ONLINE}
                    required
                    searchSelect
                  />
                )}
                <Input
                  label='Коментар'
                  id={descriptionInputId}
                  name='description'
                  multiline={true}
                  placeholder='Опишіть Вашу послугу'
                  error={errors?.description?.message}
                  {...registerAndClearOnChange('description')}
                />
              </fieldset>
              <button disabled={!isDirty || !isValid} type='submit'>
                Надіслати
              </button>
            </form>
            <ReCAPTCHA
              ref={captchaRef}
              className={css.captcha}
              sitekey={env.REACT_APP_NZ_RECAPTCHA_SITE_KEY}
              hl='uk'
              size='invisible'
            />
          </FormProvider>
        </div>
      )}
    </Modal>
  );
}
